export const NEUTRAL_KEYS = [
  "bg0",
  "bg1",
  "bg2",
  "bg3",
  "bg4",
  "fg3",
  "fg2",
  "fg1",
  "fg0",
] as const;
export const ACCENT_KEYS = [
  "red",
  "orange",
  "yellow",
  "green",
  "cyan",
  "blue",
  "purple",
  "pink",
] as const;
export const BRIGHT_KEYS = [
  "brightRed",
  "brightGreen",
  "brightYellow",
  "brightBlue",
  "brightPurple",
  "brightCyan",
] as const;

export const PALETTE_KEYS = [...NEUTRAL_KEYS, ...ACCENT_KEYS, ...BRIGHT_KEYS] as const;

export type NeutralKey = (typeof NEUTRAL_KEYS)[number];
export type AccentKey = (typeof ACCENT_KEYS)[number];
export type BrightKey = (typeof BRIGHT_KEYS)[number];
export type PaletteKey = (typeof PALETTE_KEYS)[number];

export const PALETTE_INFO: Record<PaletteKey, { label: string; hint: string }> = {
  bg0: { label: "Panel", hint: "Sidebars, floats and other recessed surfaces" },
  bg1: { label: "Base", hint: "Main background of the terminal and editor" },
  bg2: { label: "Raised", hint: "Cursor line, status bar, popup menus" },
  bg3: { label: "Selection", hint: "Visual selection and active rows" },
  bg4: { label: "Border", hint: "Borders, separators and line numbers" },
  fg3: { label: "Muted", hint: "Comments and dim text" },
  fg2: { label: "Subtle", hint: "Secondary text, punctuation" },
  fg1: { label: "Text", hint: "Main foreground" },
  fg0: { label: "Bright", hint: "Emphasis and bright white" },
  red: { label: "Red", hint: "Errors, deletions" },
  orange: { label: "Orange", hint: "Numbers, constants" },
  yellow: { label: "Yellow", hint: "Types, warnings" },
  green: { label: "Green", hint: "Strings, additions" },
  cyan: { label: "Cyan", hint: "Properties, hints" },
  blue: { label: "Blue", hint: "Functions, accent" },
  purple: { label: "Purple", hint: "Keywords" },
  pink: { label: "Pink", hint: "Macros, escapes" },
  brightRed: { label: "Bright red", hint: "ANSI 9" },
  brightGreen: { label: "Bright green", hint: "ANSI 10" },
  brightYellow: { label: "Bright yellow", hint: "ANSI 11" },
  brightBlue: { label: "Bright blue", hint: "ANSI 12" },
  brightPurple: { label: "Bright magenta", hint: "ANSI 13" },
  brightCyan: { label: "Bright cyan", hint: "ANSI 14" },
};

/** Which normal accent each bright swatch brightens (used by the "derive brights" generator). */
export const BRIGHT_SOURCE: Record<BrightKey, AccentKey> = {
  brightRed: "red",
  brightGreen: "green",
  brightYellow: "yellow",
  brightBlue: "blue",
  brightPurple: "purple",
  brightCyan: "cyan",
};

export function isPaletteKey(value: string): value is PaletteKey {
  return (PALETTE_KEYS as readonly string[]).includes(value);
}
